import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { EventCustomerBillingDTO } from '../../../../dto/event/event-customer-billing.dto';

function openProforma(row: EventCustomerBillingDTO): void {
  window.dispatchEvent(new CustomEvent('duna-customer-events-proforma', { detail: row }));
}

function openCateringWishes(row: EventCustomerBillingDTO): void {
  window.dispatchEvent(new CustomEvent('duna-customer-events-catering-wishes', { detail: row }));
}

function toggleApproved(row: EventCustomerBillingDTO): void {
  window.dispatchEvent(new CustomEvent('duna-customer-events-toggle-approved', { detail: row }));
}

function toggleProcessed(row: EventCustomerBillingDTO): void {
  window.dispatchEvent(new CustomEvent('duna-customer-events-toggle-processed', { detail: row }));
}

function formatKr(value: number | null | undefined): string {
  if (value == null) {
    return '–';
  }
  return value.toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function duna_customer_events(componentDataSourceService: ComponentDataSourceService) {
  return {
    name: 'DUNA Kunde Events',
    description: 'Indtægtsgivende events tilknyttet kunder – til faktureringsbehandling',
    execute: (params?: LoadDataParams): Observable<any> => {
      const filters = [...(params?.filters ?? [])];
      if (!filters.some(f => f.column === 'billing_processed')) {
        filters.push({
          column: 'billing_processed',
          value: 'false',
          operator: 'equals',
        });
      }

      const requestParams: LoadDataParams = {
        ...(params || ({} as any)),
        dataSourceKey: 'duna_customer_events',
        filters,
      };

      return componentDataSourceService['loadDataWithStandardFiltersGET'](
        'duna_customer_events',
        '/organizations/customers/events',
        requestParams
      ).pipe(
        catchError(error => {
          console.error('Error fetching customer events:', error);
          return of({ items: [], totalCount: 0, pageSize: 10, pageIndex: 0 });
        }),
        map(result => {
          const items = (result?.items || []) as EventCustomerBillingDTO[];
          return {
            items,
            totalCount: result?.totalCount ?? items.length,
            pageSize: result?.pageSize ?? 10,
            pageIndex: result?.pageIndex ?? 0,
            filters: result?.filters,
          };
        })
      );
    },
    settings: {
      layout: 'card',
      dense: true,
      columns: [
        {
          key: 'event_name',
          header: 'Event',
          type: 'text',
          sortable: true,
          wrap: true,
          subtitle: (row: EventCustomerBillingDTO) => row.customer?.name ?? '',
          onClick: (row: EventCustomerBillingDTO) => openProforma(row),
          tooltip: () => 'Klik for at åbne proformafaktura',
        },
        {
          key: 'start_date',
          header: 'Dato',
          type: 'date',
          sortable: true,
        },
        {
          key: 'total_price',
          header: 'Total (kr.)',
          type: 'text',
          sortable: false,
          alignment: 'right',
          transform: (v: number) => formatKr(v),
          onClick: (row: EventCustomerBillingDTO) => openProforma(row),
          tooltip: () => 'Klik for at åbne proformafaktura',
        },
        {
          key: 'proforma',
          header: '',
          type: 'button',
          buttonText: 'Proformafaktura',
          buttonColor: 'primary',
          variant: 'stroked',
          action: (row: EventCustomerBillingDTO) => openProforma(row),
        },
        {
          key: 'billing_approved_status',
          header: '',
          type: 'button',
          buttonTextFn: (row: EventCustomerBillingDTO) =>
            row.billing_approved ? 'Godkendt' : 'Ikke godkendt',
          variantFn: (row: EventCustomerBillingDTO) =>
            row.billing_approved ? 'flat' : 'stroked',
          buttonClassFn: (row: EventCustomerBillingDTO) =>
            row.billing_approved ? 'status-btn--positive' : 'status-btn--muted',
          action: (row: EventCustomerBillingDTO) => toggleApproved(row),
        },
        {
          key: 'billing_processed_status',
          header: '',
          type: 'button',
          buttonTextFn: (row: EventCustomerBillingDTO) =>
            row.billing_processed ? 'Behandlet' : 'Ubehandlet',
          variantFn: (row: EventCustomerBillingDTO) =>
            row.billing_processed ? 'flat' : 'stroked',
          buttonClassFn: (row: EventCustomerBillingDTO) =>
            row.billing_processed ? 'status-btn--positive' : 'status-btn--attention',
          action: (row: EventCustomerBillingDTO) => toggleProcessed(row),
        },
        {
          key: 'actions',
          header: '',
          type: 'menu',
          alignment: 'right',
          width: '56px',
          menuItems: [
            {
              label: 'Forplejningsønsker',
              icon: 'restaurant',
              action: (row: EventCustomerBillingDTO) => openCateringWishes(row),
            },
            {
              label: 'Rediger event',
              icon: 'open_in_new',
              action: (row: EventCustomerBillingDTO) => {
                window.open(`/edit-event/${row.guid}`, '_blank');
              }
            },
          ]
        },
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      searchable: true,
      defaultSortColumn: 'start_date',
      defaultSortDirection: 'desc',
      tableOptions: [
        {
          id: 'billing_processed',
          label: 'Behandlet',
          type: 'select',
          default: 'false',
          options: [
            { label: 'Kun ubehandlede', value: 'false' },
            { label: 'Kun behandlede', value: 'true' },
            { label: 'Alle', value: '' },
          ],
          onChange: () => {},
        },
        {
          id: 'billing_approved',
          label: 'Godkendt',
          type: 'select',
          default: '',
          options: [
            { label: 'Alle', value: '' },
            { label: 'Kun godkendte', value: 'true' },
            { label: 'Kun ikke-godkendte', value: 'false' },
          ],
          onChange: () => {},
        },
      ],
    }
  };
}
